import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Search, Tags, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { useAuth } from '../../auth/useAuth';
import { DataPanel, StatusPill } from '../../components/DataStates';
import { InlineSpinner } from '../../components/LoadingIndicators';
import { apiRequest, getApiErrorMessage } from '../../lib/api';
import { useApiCollection } from '../../lib/useApiCollection';
import type { Organization, ProductBrand } from '../../types/domain';

interface BrandDraft {
  client: string;
  name: string;
  theme_color: string;
  is_active: boolean;
}

const emptyDraft: BrandDraft = { client: '', name: '', theme_color: '', is_active: true };

export function ProductBrandsPage() {
  const { accessToken, capabilities, isPlatformAdmin, selectedWorkspace } = useAuth();
  const queryClient = useQueryClient();
  const canManage = capabilities.includes('products:manage');
  const [editing, setEditing] = useState<ProductBrand | 'new' | null>(null);
  const [draft, setDraft] = useState<BrandDraft>(emptyDraft);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 300);
    return () => window.clearTimeout(timeout);
  }, [search]);

  const params = new URLSearchParams({ ordering: 'name', page: String(page), page_size: '20' });
  if (debouncedSearch) params.set('search', debouncedSearch);
  if (selectedWorkspace?.type === 'client' && selectedWorkspace.organizationId) {
    params.set('client', selectedWorkspace.organizationId);
  }
  const brands = useApiCollection<ProductBrand>(
    'product-brands-screen',
    `/product-brands/?${params.toString()}`,
    accessToken,
  );
  const organizations = useApiCollection<Organization>(
    'product-brand-organizations',
    '/organizations/?page_size=100&ordering=name&type=client&status=active',
    accessToken,
    canManage,
  );
  const clientOptions = useMemo(() => (
    (organizations.data?.results ?? []).filter((organization) => (
      organization.type === 'client'
      && (isPlatformAdmin || organization.id === selectedWorkspace?.organizationId)
    ))
  ), [isPlatformAdmin, organizations.data?.results, selectedWorkspace?.organizationId]);

  const mutation = useMutation({
    mutationFn: () => apiRequest<ProductBrand>(
      editing === 'new' ? '/product-brands/' : `/product-brands/${editing?.id}/`,
      {
        ...(accessToken ? { accessToken } : {}),
        method: editing === 'new' ? 'POST' : 'PATCH',
        body: JSON.stringify({
          ...(editing === 'new' ? { client: draft.client } : {}),
          name: draft.name,
          theme_color: draft.theme_color.trim(),
          is_active: draft.is_active,
        }),
      },
    ),
    onSuccess: async () => {
      setEditing(null);
      setDraft(emptyDraft);
      await queryClient.invalidateQueries({ queryKey: ['collection', 'product-brands-screen'] });
      await queryClient.invalidateQueries({ queryKey: ['collection', 'product-brands'] });
      await queryClient.invalidateQueries({ queryKey: ['collection', 'product-families'] });
    },
  });

  function beginCreate() {
    mutation.reset();
    setDraft({
      ...emptyDraft,
      client: selectedWorkspace?.type === 'client'
        ? selectedWorkspace.organizationId ?? ''
        : clientOptions[0]?.id ?? '',
    });
    setEditing('new');
  }

  function beginEdit(brand: ProductBrand) {
    mutation.reset();
    setDraft({
      client: brand.client,
      name: brand.name,
      theme_color: brand.theme_color,
      is_active: brand.is_active,
    });
    setEditing(brand);
  }

  function closeForm() {
    if (mutation.isPending) return;
    setEditing(null);
    setDraft(emptyDraft);
    mutation.reset();
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    mutation.mutate();
  }

  const rows = brands.data?.results ?? [];
  const colorPickerValue = /^#[0-9A-Fa-f]{6}$/.test(draft.theme_color)
    ? draft.theme_color
    : '#2F8065';

  return (
    <>
      <header className="page-heading">
        <div>
          <p className="eyebrow">Catalogue</p>
          <h1>Product brands</h1>
          <p>Maintain consistent client-owned brands for product families and reporting.</p>
        </div>
        <span className="record-count"><Tags size={16} /> {brands.data?.count ?? 0} brands</span>
      </header>

      <div className="catalogue-list-toolbar">
        <label className="search-field">
          <Search aria-hidden="true" size={17} />
          <input aria-label="Search product brands" onChange={(event) => setSearch(event.target.value)} placeholder="Search brand or client" type="search" value={search} />
        </label>
        {canManage && clientOptions.length > 0 ? <button className="button primary" onClick={beginCreate} type="button"><Plus size={17} /> Add brand</button> : null}
      </div>

      <DataPanel loading={brands.isLoading} error={brands.isError} empty={rows.length === 0}>
        <section className="content-card data-card">
          <div className="data-table-wrap"><table className="data-table">
            <thead><tr><th>Brand</th><th>Client</th><th>Chart colour</th><th>Families</th><th>Status</th><th><span className="sr-only">Actions</span></th></tr></thead>
            <tbody>{rows.map((brand) => <tr key={brand.id}>
              <td><strong>{brand.name}</strong></td>
              <td>{brand.client_name}</td>
              <td>{brand.theme_color ? <span className="brand-theme-colour"><i style={{ backgroundColor: brand.theme_color }} />{brand.theme_color}</span> : <span className="muted-cell">Default palette</span>}</td>
              <td>{brand.family_count}</td>
              <td><StatusPill value={brand.is_active ? 'active' : 'inactive'} /></td>
              <td><div className="table-actions">{canManage && (isPlatformAdmin || brand.client === selectedWorkspace?.organizationId) ? <button className="text-button" onClick={() => beginEdit(brand)} type="button"><Pencil size={14} /> Edit</button> : null}</div></td>
            </tr>)}</tbody>
          </table></div>
          <div className="attendance-pagination"><span>Page {page} · {brands.data?.count ?? 0} records</span><div><button className="button secondary" disabled={!brands.data?.previous} onClick={() => setPage((value) => Math.max(1, value - 1))} type="button">Previous</button><button className="button secondary" disabled={!brands.data?.next} onClick={() => setPage((value) => value + 1)} type="button">Next</button></div></div>
        </section>
      </DataPanel>

      {editing ? (
        <div className="confirmation-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) closeForm(); }} role="presentation">
          <section aria-labelledby="product-brand-form-title" aria-modal="true" className="confirmation-dialog catalogue-form-dialog" role="dialog">
            <button aria-label="Close brand form" className="icon-button confirmation-close" disabled={mutation.isPending} onClick={closeForm} type="button"><X size={19} /></button>
            <div className="card-heading"><div><h2 id="product-brand-form-title">{editing === 'new' ? 'Add product brand' : `Edit ${editing.name}`}</h2><p>Brands belong to one client and keep family-level reporting consistent.</p></div></div>
            <form className="management-form" onSubmit={submit}>
              <div className="form-grid">
                <label>Client<select disabled={editing !== 'new'} required value={draft.client} onChange={(event) => setDraft({ ...draft, client: event.target.value })}><option value="">e.g. PepsiCo Pakistan</option>{clientOptions.map((client) => <option key={client.id} value={client.id}>{client.name}</option>)}</select></label>
                <label>Brand name<input placeholder="e.g. Pepsi" required value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} /></label>
                <label className="checkbox-field"><input checked={Boolean(draft.theme_color)} type="checkbox" onChange={(event) => setDraft({ ...draft, theme_color: event.target.checked ? '#2F8065' : '' })} /> Use a custom chart colour</label>
                {draft.theme_color ? <label>Brand chart colour<span className="brand-theme-colour-inputs"><input aria-label="Brand chart colour picker" onChange={(event) => setDraft({ ...draft, theme_color: event.target.value.toUpperCase() })} type="color" value={colorPickerValue} /><input aria-label="Brand chart colour" maxLength={7} onChange={(event) => setDraft({ ...draft, theme_color: event.target.value.toUpperCase() })} pattern="#[0-9A-Fa-f]{6}" placeholder="#2F8065" required type="text" value={draft.theme_color} /></span><small className="brand-theme-colour-hint">Used for this brand in charts; product families receive derived shades.</small></label> : null}
                <label className="checkbox-field"><input checked={draft.is_active} type="checkbox" onChange={(event) => setDraft({ ...draft, is_active: event.target.checked })} /> Available for product families</label>
              </div>
              {mutation.isError ? <p className="form-error" role="alert">{getApiErrorMessage(mutation.error)}</p> : null}
              <div className="form-actions"><button className="button secondary" disabled={mutation.isPending} onClick={closeForm} type="button">Cancel</button><button className="button primary" disabled={!draft.client || mutation.isPending} type="submit">{mutation.isPending ? <InlineSpinner /> : null}{editing === 'new' ? 'Add brand' : 'Save brand'}</button></div>
            </form>
          </section>
        </div>
      ) : null}
    </>
  );
}
